import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import {
  FiArchive,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiGrid,
  FiList,
  FiSettings,
  FiSidebar,
  FiTag,
} from "react-icons/fi";
import { boardPublicPath } from "@/config/browserBoard";
import type { ThreadSummary } from "@/entities/thread";
import { DesktopCatalogSortNavigation } from "@/features/catalog/components/navigation/DesktopCatalogSortNavigation";
import { SearchBar } from "@/features/catalog/components/SearchBar";
import { TagFilter } from "@/features/catalog/components/TagFilter";
import type { SortType } from "@/features/catalog/stores/catalogStore";
import { DesktopRefreshLabel } from "@/shared/ui/navigation/DesktopRefreshLabel";

interface DesktopCatalogToolbarProps {
  activeMode?: "board" | "catalog";
  tagFilterThreads: ThreadSummary[];
  currentSort: SortType;
  selectSortMethod: (sort: SortType) => void;
  autoReload: boolean;
  setAutoReload: (enabled: boolean) => void;
  isFetching: boolean;
  onRefresh: () => void;
  onToggleSidebar: () => void;
  onScrollToTop: () => void;
  onScrollToBottom: () => void;
  onSettingsClick: React.MouseEventHandler<HTMLAnchorElement>;
  onOpenThreadCreate: () => void;
}

/**
 * デスクトップ版カタログの操作を、参照デザインの1段ツールバーへ集約する。
 * 各操作の既存ロジックは親から受け取り、このコンポーネントでは見た目と構造だけを担う。
 */
export function DesktopCatalogToolbar({
  activeMode = "catalog",
  tagFilterThreads,
  currentSort,
  selectSortMethod,
  autoReload,
  setAutoReload,
  isFetching,
  onRefresh,
  onToggleSidebar,
  onScrollToTop,
  onScrollToBottom,
  onSettingsClick,
}: DesktopCatalogToolbarProps): React.ReactElement {
  const [isTagFilterExpanded, setIsTagFilterExpanded] = useState(false);
  const tagFilterPanelId = useId();

  return (
    <nav
      className="desktop-catalog-toolbar"
      aria-label={
        activeMode === "catalog"
          ? "カタログナビゲーション"
          : "リストナビゲーション"
      }
    >
      <fieldset
        className="desktop-catalog-toolbar-navigation"
        aria-label="ページ内移動"
      >
        <button
          className="desktop-catalog-toolbar-icon"
          type="button"
          onClick={onToggleSidebar}
          aria-label="履歴サイドバーを切り替える"
        >
          <FiSidebar aria-hidden="true" />
        </button>
        <button
          className="desktop-catalog-toolbar-icon"
          type="button"
          onClick={onScrollToTop}
          aria-label="ページ上部へ移動"
        >
          <FiChevronUp aria-hidden="true" />
        </button>
        <button
          className="desktop-catalog-toolbar-icon"
          type="button"
          onClick={onScrollToBottom}
          aria-label="ページ下部へ移動"
        >
          <FiChevronDown aria-hidden="true" />
        </button>
      </fieldset>

      <fieldset
        className="desktop-catalog-toolbar-primary"
        aria-label="カタログ表示"
      >
        <Link
          className="desktop-catalog-toolbar-link"
          to="/board"
          aria-current={activeMode === "board" ? "page" : undefined}
        >
          <FiList aria-hidden="true" />
          リスト
        </Link>
        <a
          className="desktop-catalog-toolbar-link"
          href={boardPublicPath("/")}
          aria-current={activeMode === "catalog" ? "page" : undefined}
        >
          <FiGrid aria-hidden="true" />
          カタログ
        </a>
        <DesktopCatalogSortNavigation
          currentSort={currentSort}
          selectSortMethod={selectSortMethod}
        />
      </fieldset>

      <button
        className="desktop-catalog-toolbar-action"
        type="button"
        aria-expanded={isTagFilterExpanded}
        aria-controls={tagFilterPanelId}
        onClick={(): void => setIsTagFilterExpanded((expanded) => !expanded)}
      >
        <FiTag aria-hidden="true" />
        フィルタ
        {isTagFilterExpanded ? (
          <FiChevronUp aria-hidden="true" />
        ) : (
          <FiChevronDown aria-hidden="true" />
        )}
      </button>

      <fieldset
        className="desktop-catalog-toolbar-secondary"
        aria-label="カタログ操作"
      >
        <a
          className="desktop-catalog-toolbar-link"
          href={boardPublicPath("/history/viewed")}
        >
          <FiClock aria-hidden="true" />
          履歴
        </a>
        <a
          className="desktop-catalog-toolbar-link"
          href={boardPublicPath("/archive")}
        >
          <FiArchive aria-hidden="true" />
          過去ログ
        </a>
        <a
          className="desktop-catalog-toolbar-link"
          href={boardPublicPath("/settings")}
          onClick={onSettingsClick}
        >
          <FiSettings aria-hidden="true" />
          設定
        </a>
        <label className="desktop-auto-reload">
          自動更新
          <input
            className="sr-only"
            type="checkbox"
            role="switch"
            aria-checked={autoReload}
            checked={autoReload}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
              setAutoReload(event.currentTarget.checked)
            }
          />
        </label>
        <button
          className="desktop-catalog-toolbar-action desktop-catalog-toolbar-refresh"
          type="button"
          disabled={isFetching}
          onClick={onRefresh}
        >
          <DesktopRefreshLabel isFetching={isFetching} />
        </button>
      </fieldset>

      <SearchBar className="desktop-catalog-toolbar-search" />

      {isTagFilterExpanded && (
        <section
          id={tagFilterPanelId}
          className="desktop-catalog-toolbar-tag-filter"
          aria-label="フィルタ"
        >
          <TagFilter
            threads={tagFilterThreads}
            className="desktop-tag-filter"
          />
        </section>
      )}
    </nav>
  );
}
